import { graphql } from 'gql';

export const bimContractGql = graphql(`
    query bimContract($contractId: Long!) {
        bimContract(contractId: $contractId) {
            data {
                hasNoPlanByConstructionType
                authoredBy
                constructionTypes {
                    id
                    code
                    name
                    shortName
                    usedInObjectStructure
                }
                contractType
                conversionIndices {
                    coefficients {
                        kozp
                        kemm
                        kmtr
                        keqp
                        knr
                        ksp
                        kpir
                        kzpm
                        kmz
                        koz
                    }
                    constructionType {
                        id
                        code
                        name
                        shortName
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    id
                }
                createdAt
                generalInfo {
                    amountWithTax
                    amountWithoutTax
                    contractor
                    contractorAddress
                    contractorOKPO
                    customer
                    customerAddress
                    customerOKPO
                    finishDate
                    internalNumber
                    name
                    note
                    okpd
                    okud
                    signingDate
                    startDate
                    subject
                    tax
                }
                id
                limitedCosts {
                    constructionType {
                        id
                        code
                        name
                        shortName
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    items {
                        id
                        rateValue
                        type
                        fotValue
                    }
                    id
                    regionalFactor
                }
                otherDistributions {
                    contractSeason {
                        id
                        year
                    }
                    id
                    seasonValue
                }
                plan {
                    chapter12
                    chapter1_7
                    chapter8
                    chapter9
                    equipmentContractor
                    equipmentCustomer
                    materialContractor
                    materialCustomer
                    northernAllowance
                }
                projectUniqueId
                seasons {
                    id
                    year
                }
                seasonsInflation {
                    contractSeason {
                        id
                        year
                    }
                    id
                    inflationRate
                }
                smrDistributions {
                    contractSeason {
                        id
                        year
                    }
                    id
                    seasonValue
                }
                status
                supplyBasis
                uniqueId
                limitedCostCalcMethod
                hasObjectStructure
                statusFollowers
                resultPlan {
                    constructionTypePlanResults {
                        constructionTypeId
                        contractPlan
                    }
                    name
                    totalType
                    isHeadLine
                    isEditable
                    contractPlan
                    hasSeason
                    contractSeasonYear
                    isLimitPrice
                }
                hasFilledAddresses
                cost {
                    contractPlan
                    contractPlanReminderPercent
                    contractPlanReminderWithTax
                    contractPlanTax
                    contractPlanWithTax
                    deviationTip
                    workOrdersPlanCost
                    workOrdersPlanCostWithTax
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const bimContractDefaultsGql = graphql(`
    query bimContractDefaults($projectUniqueId: String!) {
        bimContractDefaults(projectUniqueId: $projectUniqueId) {
            data {
                status
                contractType
                customer
                authoredBy
                resultPlan {
                    constructionTypePlanResults {
                        constructionTypeId
                        contractPlan
                    }
                    name
                    totalType
                    isHeadLine
                    isEditable
                    contractPlan
                    hasSeason
                    contractSeasonYear
                    isLimitPrice
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const bimCreateContractGql = graphql(`
    mutation bimCreateContract($contractInput: BimContractCreateInput!) {
        bimCreateContract(contractInput: $contractInput) {
            data {
                id
                uniqueId
                projectUniqueId
                status
                contractType
                constructionTypes {
                    id
                    name
                }
                seasons {
                    id
                    year
                }
                generalInfo {
                    internalNumber
                    subject
                    customer
                    contractor
                }
                conversionIndices {
                    id
                    constructionType {
                        id
                        name
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    coefficients {
                        kozp
                        kemm
                        kmz
                        koz
                    }
                }
                seasonsInflation {
                    id
                    contractSeason {
                        id
                        year
                    }
                    inflationRate
                }
                limitedCosts {
                    id
                    constructionType {
                        id
                        name
                        catalogId
                        fullName
                        contractFileName
                        createdAt
                    }
                    regionalFactor
                    items {
                        type
                        rateValue
                    }
                }
                plan {
                    chapter1_7
                    chapter9
                    equipmentCustomer
                }
                smrDistributions {
                    id
                    contractSeason {
                        id
                        year
                    }
                    seasonValue
                }
                otherDistributions {
                    id
                    contractSeason {
                        id
                        year
                    }
                    seasonValue
                }
                createdAt
                authoredBy
            }
            message
            validation {
                property
                message
            }
            result
            code
        }
    }
`);

export const bimUpdateContractGql = graphql(`
    mutation bimUpdateContract($contractInput: BimContractUpdateInput!) {
        bimUpdateContract(contractInput: $contractInput) {
            hideValidation
            hasValidation
            validation {
                property
                message
            }
            data {
                id
            }
            result
            message
            code
        }
    }
`);

export const bimUniqContractInternalNumberGql = graphql(`
    query bimIsUniqueContractInternalNumber($contractId: Long, $internalNumber: String!) {
        bimIsUniqueContractInternalNumber(contractId: $contractId, internalNumber: $internalNumber) {
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const bimFrameContractResultsGql = graphql(`
    query bimFrameContractResults($contractId: Long!) {
        bimFrameContractResults(contractId: $contractId) {
            data {
                workOrders {
                    signingDate
                    internalNumber
                    status
                    actKC2Navs {
                        id
                        date
                        number
                        status
                        season {
                            id
                            year
                        }
                        costGroup
                    }
                    actKC3Navs {
                        id
                        number
                        date
                        actsKC2 {
                            id
                            date
                            number
                            status
                            season {
                                id
                                year
                            }
                            costGroup
                        }
                    }
                    workOrderChanges {
                        id
                        signingDate
                        workOrderNumber
                        workOrderChangeNumber
                        status
                    }
                    workOrderChange {
                        id
                        signingDate
                        workOrderNumber
                        workOrderChangeNumber
                        status
                    }
                    navLinkId
                    name
                    metadata
                    id
                }
                contractResults {
                    workOrderPlanSum
                    workOrderResults {
                        workOrderId
                        planPrice
                        completedPrice
                        deviation
                        constructionTypeResults {
                            constructionTypeId
                            completedPrice
                            planPrice
                        }
                    }
                    name
                    isHeadLine
                    contractPlan
                    contractRemainder
                    deviation
                    deviationTip
                    totalType
                    completed
                    completedDeviation
                    completedRemainder
                    completedRemainderDeviationTip
                    constructionTypePlanResults {
                        constructionTypeId
                        contractPlan
                    }
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);

export const bimNormalContractResultsGql = graphql(`
    query bimNormalContractResults($contractId: Long!) {
        bimNormalContractResults(contractId: $contractId) {
            data {
                actKC2CompletedSum
                name
                isHeadLine
                contractPlan
                contractRemainder
                deviation
                deviationTip
                totalType
                constructionTypeCompletedResult {
                    deviation
                    completedPrice
                    constructionTypeId
                }
                constructionTypePlanResults {
                    constructionTypeId
                    contractPlan
                }
            }
            result
            message
            buttonText
            hasValidation
            hideValidation
            validation {
                property
                message
            }
        }
    }
`);
